import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ChatAction } from '../types';
import type { MerchantPreset } from '../merchants/types';
import type {
  BusinessActionCapabilities,
  BusinessActionContext,
  BusinessActionMessageArgs,
} from '../merchants/businessActions';
// The generic button/form handlers only run on a click or form submit, so they live in a lazy
// chunk (keeps the main bundle under its size budget). The load starts when the hook mounts, so
// it has resolved long before the first click.
type GenericActionsModule = typeof import('../merchants/genericBusinessActions');
let genericActionsPromise: Promise<GenericActionsModule> | null = null;
function loadGenericActions(): Promise<GenericActionsModule> {
  if (!genericActionsPromise) {
    genericActionsPromise = import(
      /* webpackChunkName: "business-actions" */ '../merchants/genericBusinessActions'
    ).catch(error => {
      genericActionsPromise = null;
      throw error;
    });
  }
  return genericActionsPromise;
}
import { NEUTRAL_MERCHANT_PRESET } from '../merchants/neutral';
import { loadMerchantPreset } from '../merchants/registry';
import { normalizeActionValue } from '../utils/actionFilters';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../utils/i18n';

const INPUT_DISABLED_ERROR = 'Please complete the required steps before sending.';
/** `__sentinel__` action values are canonical and never re-mapped through chip labels. */
const SENTINEL_VALUE = /^__.+__$/;

/**
 * Canonical action value for a click: sentinels pass through untouched; a
 * human-readable chip label is looked up (normalized) in `labelValues`.
 */
export function resolveBusinessActionValue(
  baseValue: string,
  normalized: string,
  labelValues: Record<string, string>
): string {
  if (SENTINEL_VALUE.test(baseValue)) return baseValue;
  return labelValues[normalized] ?? baseValue;
}

interface UseBusinessActionsOptions {
  chatDispatch: (action: ChatAction) => void;
  chatLoading: boolean;
  createMessage: (args: BusinessActionMessageArgs) => Promise<unknown>;
  threadId: string | null;
  createThread:
    | ((args?: {
        subject?: string;
        message?: string;
        meta?: Record<string, unknown>;
        ch?: string;
      }) => Promise<{ threadId: string; raw: unknown }>)
    | null;
  threadCreateEndpoint: string | null | undefined;
  subject: string;
  companyName: string;
  channel: string;
  allowAttachments: boolean;
  inputDisabled: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onHandoffRequest?: (context: {
    threadId?: string | null;
    preChatData?: Record<string, string>;
  }) => void;
  onSelfieUploadIntent?: () => void;
  onStartNewChat?: () => void;
  preChatData?: Record<string, string>;
  trackAnalytics?: (action: string, data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
  /** Widget string table for generic copy (Yes / No, signup); merchant copy stays inline. */
  strings?: WidgetStrings;
  /** Active merchant preset; its `businessActions` run before the generic flows. Defaults to neutral. */
  merchantPreset?: MerchantPreset;
  /**
   * False while the lazy preset chunk is still loading. A click that arrives
   * before it resolves awaits `loadMerchantPreset(id)` so merchant handlers are
   * never missed; a failed load falls back to the (static) preset given.
   */
  merchantPresetLoaded?: boolean;
}

interface UseBusinessActionsReturn {
  handleButtonClick: (value: string) => Promise<void>;
  handleFormSubmit: (formId: string | undefined, data: Record<string, string>) => Promise<void>;
}

/**
 * Generic dispatcher for quick-action buttons and rich-message forms. Builds a
 * capability context, resolves the clicked label to a canonical value, then runs
 * the first matching handler from `[...merchantPreset.businessActions.handlers,
 * ...GENERIC_HANDLERS]`.
 */
export function useBusinessActions({
  chatDispatch,
  chatLoading,
  createMessage,
  threadId,
  createThread,
  threadCreateEndpoint,
  subject,
  companyName,
  channel,
  allowAttachments,
  inputDisabled,
  fileInputRef,
  onHandoffRequest,
  onSelfieUploadIntent,
  onStartNewChat,
  preChatData,
  trackAnalytics,
  onError,
  strings = DEFAULT_WIDGET_STRINGS,
  merchantPreset = NEUTRAL_MERCHANT_PRESET,
  merchantPresetLoaded = true,
}: UseBusinessActionsOptions): UseBusinessActionsReturn {
  const actionInFlightRef = useRef(false);

  useEffect(() => {
    loadGenericActions().catch(() => {
      // Retried on the first click; a failed prefetch must not surface as an error.
    });
  }, []);

  const ensureThreadForAction = useCallback(async () => {
    if (threadId) return threadId;
    if (!createThread) return null;
    try {
      const created = await createThread({
        subject: subject || (companyName ? `New message from ${companyName}` : 'New message'),
        ch: channel,
      });
      return created?.threadId || null;
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [threadId, createThread, subject, companyName, channel, onError]);

  const safeCreateMessage = useCallback(
    async (args: BusinessActionMessageArgs) => {
      chatDispatch({ type: 'SEND_START' });
      try {
        await createMessage(args);
        chatDispatch({ type: 'SEND_SUCCESS' });
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        const userMessage =
          errorObj.message && errorObj.message !== 'Failed to fetch'
            ? `Failed to send: ${errorObj.message}`
            : 'Failed to send. Please try again.';
        chatDispatch({ type: 'SEND_ERROR', error: userMessage });
        onError?.(errorObj);
      }
    },
    [createMessage, chatDispatch, onError]
  );

  const capabilities = useMemo<BusinessActionCapabilities>(
    () => ({
      chatDispatch,
      createMessage,
      safeCreateMessage,
      ensureThreadForAction,
      threadId,
      threadCreateEndpoint,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      onHandoffRequest,
      onSelfieUploadIntent,
      onStartNewChat,
      preChatData,
      trackAnalytics,
      onError,
      strings,
    }),
    [
      chatDispatch,
      createMessage,
      safeCreateMessage,
      ensureThreadForAction,
      threadId,
      threadCreateEndpoint,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      onHandoffRequest,
      onSelfieUploadIntent,
      onStartNewChat,
      preChatData,
      trackAnalytics,
      onError,
      strings,
    ]
  );

  /** The preset whose handlers apply to this interaction (awaits the lazy chunk if needed). */
  const resolvePreset = useCallback(async (): Promise<MerchantPreset> => {
    if (merchantPresetLoaded) return merchantPreset;
    try {
      return await loadMerchantPreset(merchantPreset.id);
    } catch {
      return merchantPreset;
    }
  }, [merchantPreset, merchantPresetLoaded]);

  const handleButtonClick = useCallback(
    async (value: string) => {
      if (!value) return;
      if (chatLoading || actionInFlightRef.current) return;
      actionInFlightRef.current = true;
      try {
        const [rawValue, ...rest] = value.split('|');
        const baseValue = rawValue.trim();
        const normalized = normalizeActionValue(baseValue);
        const payload = rest.join('|').trim();
        const metaExtras = payload ? { orderId: payload } : undefined;
        trackAnalytics?.('widget_action_click', {
          action: 'quick_action',
          value: baseValue,
          label: normalized,
        });
        const preset = merchantPresetLoaded ? merchantPreset : await resolvePreset();
        const merchantActions = preset.businessActions;
        const { GENERIC_HANDLERS, GENERIC_LABEL_VALUES } = await loadGenericActions();
        const labelValues = { ...GENERIC_LABEL_VALUES, ...(merchantActions?.labelValues ?? {}) };
        const handlers = [...(merchantActions?.handlers ?? []), ...GENERIC_HANDLERS];
        const ctx: BusinessActionContext = {
          ...capabilities,
          baseValue,
          normalized,
          value: resolveBusinessActionValue(baseValue, normalized, labelValues),
          payload,
          metaExtras,
          resolvedThreadId: threadId,
        };
        // Messages a handler sends go to the action's thread. Without this, a first quick reply
        // created a thread (ensureThreadForAction) and then sent its message with the stale
        // empty thread id, so the server opened a second thread; whichever id the widget kept
        // last split the conversation (and lost order verification) between the two.
        // (Only a thread created here needs passing: an existing one is createMessage's default.)
        const withActionThread = (args: BusinessActionMessageArgs): BusinessActionMessageArgs =>
          args.tid || !ctx.resolvedThreadId || ctx.resolvedThreadId === threadId
            ? args
            : { ...args, tid: ctx.resolvedThreadId };
        ctx.createMessage = args => capabilities.createMessage(withActionThread(args));
        ctx.safeCreateMessage = args => capabilities.safeCreateMessage(withActionThread(args));
        const handler = handlers.find(candidate => candidate.matches(ctx));
        if (!handler) return;
        if (handler.requiresInput && inputDisabled) {
          chatDispatch({ type: 'SEND_ERROR', error: INPUT_DISABLED_ERROR });
          return;
        }
        if (handler.requiresThread && threadCreateEndpoint) {
          ctx.resolvedThreadId = await ensureThreadForAction();
        }
        await handler.run(ctx);
      } finally {
        actionInFlightRef.current = false;
      }
    },
    [
      capabilities,
      chatLoading,
      chatDispatch,
      inputDisabled,
      ensureThreadForAction,
      threadCreateEndpoint,
      threadId,
      trackAnalytics,
      merchantPreset,
      merchantPresetLoaded,
      resolvePreset,
    ]
  );

  const handleFormSubmit = useCallback(
    async (formId: string | undefined, data: Record<string, string>) => {
      const preset = merchantPresetLoaded ? merchantPreset : await resolvePreset();
      const { GENERIC_FORM_HANDLERS } = await loadGenericActions();
      const formHandlers = [
        ...(preset.businessActions?.formHandlers ?? []),
        ...GENERIC_FORM_HANDLERS,
      ];
      const handler = formHandlers.find(candidate => candidate.formId === formId);
      if (!handler) return;
      await handler.run({ ...capabilities, formId: handler.formId, data });
    },
    [capabilities, merchantPreset, merchantPresetLoaded, resolvePreset]
  );

  return { handleButtonClick, handleFormSubmit };
}
